import { useQuery } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";

import type { AddressUploadProgressUpdateDto, MassAddressAddressCreateRequest } from "@/api/types";
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { UploadFileButton } from "@/components/Button/UploadFileButton";
import { DataTable } from "@/components/DataTable/DataTable";
import { type Column, type Data, parseData } from "@/components/DataTable/parser";
import { downloadExcel, parseExcel } from "@/components/DataTable/sheet";
import { validate } from "@/components/DataTable/validation";
import { ErrorPage } from "@/components/Error/ErrorPage";
import { useFlashToast } from "@/components/FlashToast/FlashToast";
import { DotsGridIcon } from "@/components/Icons/DotsGridIcon";
import { Download01Icon } from "@/components/Icons/Download01Icon";
import { DocumentPage } from "@/components/Page/DocumentPage";
import { PageGrid } from "@/components/PageGrid/PageGrid";
import { Button } from "@/design-system/Button";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { Inline } from "@/design-system/Inline";
import { Notice } from "@/design-system/Notice";
import { SectionCard } from "@/design-system/SectionCard";
import { HOUSE_NUMBER_WITH_SUFFIX_REGEX, splitCombinedAddressColumn, splitHouseNumber } from "@/helpers/address";
import { parseFileContent } from "@/helpers/file-parse";
import { isAbortError } from "@/helpers/isAbortError";
import { processJsonStream } from "@/helpers/processJsonStream";
import { isDefined } from "@/helpers/util";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useMutationAbortController } from "@/hooks/useMutationAbortController";
import { useOnDropFiles } from "@/hooks/useOnDropFiles";
import { useOnPaste } from "@/hooks/useOnPaste";
import { usePrompt } from "@/hooks/usePrompt";
import { fileMutations } from "@/queries/files/mutations";
import { portfolioMutations } from "@/queries/portfolio/mutations";
import { usePortfolioQueries } from "@/queries/portfolio/queryOptions";
import { useTranslation } from "@/translations";

import { BulkUploadAddressesInstructionView } from "./components/BulkUploadAddressesInstructionView";
import {
  BulkUploadAddressesSubmissionResultView,
  type PortfolioBulkUploadAddressesSubmissionSummary,
} from "./components/BulkUploadAddressesSubmissionResultView";
import { BulkUploadAddressesSubmissionSummaryView } from "./components/BulkUploadAddressesSubmissionSummaryView";
import { BulkUploaadAddressesSubmittingView } from "./components/BulkUploadAddressesSubmittingView";
import { BulkUploadAddressesValidationResultView } from "./components/BulkUploadAddressesValidationResultView";

// A safe number for most devices to handle
const MAX_AMOUNT_CORRECTION_ROWS = 300;

enum PageState {
  INITIAL = "initial",
  VALIDATING = "validating",
  VALIDATED = "validated",
  SUBMITTING = "submitting",
  SUBMITTED_WITH_POTENTIAL_CORRECTIONS = "submitted-with-potential-corrections",
  COMPLETED = "completed",
}

export function BulkUploadAddressesPage(): React.ReactNode {
  const [isParsingFile, setIsParsingFile] = useState(false);
  const [isAllRowsProcessed, setIsAllRowsProcessed] = useState(false);
  const [data, setData] = useState<Data<NonNullable<typeof columns>>>();
  const [uploadedFile, setUploadedFile] = useState<File>();
  const [submissionState, setSubmissionState] = useState({
    isSubmitted: false,
    progress: 0,
  });
  const [submissionSummary, setSubmissionSummary] = useState<PortfolioBulkUploadAddressesSubmissionSummary>({
    amountCurrSubmitting: 0,
    amountSuccess: 0,
    amountDuplicates: 0,
    amountFailure: 0,
  });

  const { t } = useTranslation();
  const sessionUser = useSessionUser();
  const showFlashToast = useFlashToast();
  const portfolioQueries = usePortfolioQueries();
  let pageState: PageState = PageState.INITIAL;

  usePrompt(t("component.prompt.confirmation"), pageState !== PageState.INITIAL);

  const {
    data: permittedProjects,
    isPending: isPendingPermittedProjects,
    error: permittedProjectsError,
  } = useQuery({
    ...portfolioQueries.massAddressesProjects(),
    refetchOnWindowFocus: pageState === PageState.INITIAL,
  });
  const {
    data: permittedBuildings,
    isPending: isPendingPermittedBuildings,
    error: permittedBuildingsError,
  } = useQuery({
    ...portfolioQueries.massAddressesBuildings(),
    refetchOnWindowFocus: pageState === PageState.INITIAL,
  });
  const abortController = useMutationAbortController();
  const addMassAddressesMutation = portfolioMutations.useAddMassAddresses();
  const { mutateAsync: uploadDocumentAsync } = fileMutations.useAddDocument();

  const columns = useMemo(() => {
    return [
      {
        name: "Project",
        alias: ["project", "project name", "projectnaam"],
        rules: {
          required: t("page.address-bulk-upload.validation.project.required"),
          oneOf: {
            error: t("page.address-bulk-upload.validation.project.not-one-of"),
            values: () => permittedProjects?.map((x) => x.name) || [],
          },
        },
      },
      {
        name: "Building",
        alias: ["gebouw", "gebouw naam", "building name"],
        rules: {
          required: t("page.address-bulk-upload.validation.building.required"),
          oneOf: {
            error: t("page.address-bulk-upload.validation.building.not-one-of"),
            values: (row) =>
              permittedBuildings?.filter((building) => building.projectName === row["Project"]).map((x) => x.name) ||
              [],
          },
        },
      },
      {
        name: "Street",
        alias: ["straatnaam", "straat", "street name", "streetname", "adres", "address"],
        rules: { required: t("page.address-bulk-upload.validation.street-name.required") },
      },
      {
        name: "House number",
        alias: ["huisnummer", "housenumber", "huisnr", "huisnr."],
        rules: {
          required: t("page.address-bulk-upload.validation.house-number.required"),
          match: {
            error: t("page.address-bulk-upload.validation.house-number.is-not-number"),
            regex: HOUSE_NUMBER_WITH_SUFFIX_REGEX,
          },
        },
      },
      {
        name: "House number suffix",
        alias: [
          "house number suffix",
          "huisnummertoevoeging",
          "huisnummer toevoeging",
          "toevoeging",
          "huisnr toevoeging",
        ],
      },
      {
        name: "Postal code",
        alias: ["postcode", "zipcode", "zip"],
        rules: { required: t("page.address-bulk-upload.validation.postal-code.required") },
      },
      {
        name: "City",
        alias: ["stad", "stad naam", "city name", "plaats", "plaatsnaam"],
        rules: { required: t("page.address-bulk-upload.validation.city.required") },
      },
      { name: "Type", alias: ["woning type", "address type", "eigenaar"] },
      { name: "Floor", alias: ["verdieping", "floor", "level"] },
    ] satisfies Column[];
  }, [permittedBuildings, t, permittedProjects]);
  const dataState = useMemo(() => (data && columns ? validate(columns, data) : undefined), [data, columns]);

  async function submitRows({ rows }: { rows: number[] }) {
    if (!data) {
      throw new Error("Data is not loaded");
    }

    if (!permittedBuildings || !permittedProjects) {
      throw new Error("Permitted buildings or projects are not loaded");
    }

    // Upload the file to the backend first (for logging/audit purposes)
    if (uploadedFile) {
      await uploadDocumentAsync({ file: uploadedFile });
    }

    const payloadAddresses: MassAddressAddressCreateRequest[] = [];

    for (const row of data) {
      const currIndex = data.indexOf(row);

      if (!rows.includes(currIndex)) {
        continue;
      }

      const rowData = row as { [k in NonNullable<typeof columns>[number]["name"]]: string };

      // The house number column may carry a glued-on suffix (e.g. "12a"), so split it into its
      // number + suffix. An explicit "House number suffix" column takes precedence over a glued one.
      const parsedHouseNumber = splitHouseNumber(rowData["House number"] ?? "");

      // Should not happen due to table column validation rules
      if (!parsedHouseNumber) {
        throw new Error("Invalid house number");
      }

      // There could be multiple projects and buildings with the same name
      // therefore we need to find the right pair
      const projectIds = permittedProjects.filter((project) => project.name === rowData["Project"]).map((x) => x.id);
      let projectId = projectIds[0];
      let buildingId = "";
      for (const building of permittedBuildings) {
        if (building.name === rowData["Building"] && projectIds.includes(building.projectId)) {
          projectId = building.projectId;
          buildingId = building.id;
          break;
        }
      }

      if (!projectId) {
        throw new Error("Project does not exist");
      }

      if (!buildingId) {
        throw new Error("Building does not exist (in this project)");
      }

      payloadAddresses.push({
        projectId,
        buildingId,
        city: rowData["City"],
        floor: rowData["Floor"] || undefined,
        housenumber: parsedHouseNumber.houseNumber,
        housenumberSuffix: rowData["House number suffix"] || parsedHouseNumber.houseNumberSuffix,
        postalCode: rowData["Postal code"],
        streetName: rowData["Street"],
        type: rowData["Type"] || undefined,
      } satisfies MassAddressAddressCreateRequest);
    }

    setSubmissionState((currState) => ({
      ...currState,
      progress: 0,
    }));
    setSubmissionSummary((currState) => ({
      ...currState,
      amountCurrSubmitting: rows.length,
    }));

    try {
      const response = await addMassAddressesMutation.mutateAsync({
        payload: {
          addresses: payloadAddresses,
        },
        signal: abortController.getSignal(),
      });

      if (!response.ok || !response.body) {
        setSubmissionSummary((currState) => ({
          ...currState,
          amountCurrSubmitting: 0,
        }));
        throw new Error("Failed to retrieve upload progress");
      }

      await processJsonStream<AddressUploadProgressUpdateDto>({
        stream: response.body,
        callbacks: {
          onChunk: (result) => {
            setSubmissionSummary((currState) => ({
              ...currState,
              amountDuplicates: currState.amountDuplicates + (result.state === "duplicate" ? 1 : 0),
              amountSuccess: currState.amountSuccess + (result.state === "success" ? 1 : 0),
              amountFailure: currState.amountFailure + (result.state === "error" ? 1 : 0),
            }));

            setSubmissionState((currState) => ({
              ...currState,
              progress: (result.index / (payloadAddresses.length - 1)) * 100,
            }));
          },
          onDone: () => {
            showFlashToast({
              message: t("page.portfolio-address-bulk-upload.notification.success"),
              variant: "success",
            });

            setSubmissionSummary((currState) => ({
              ...currState,
              amountCurrSubmitting: 0,
            }));
          },
          onError: () => {
            showFlashToast({ variant: "alert", message: t("page.portfolio-address-bulk-upload.notification.error") });
          },
        },
      });
    } catch (error) {
      if (!isAbortError(error)) {
        showFlashToast({ variant: "alert", message: t("page.portfolio-address-bulk-upload.notification.error") });
      }
    }
  }

  async function handleSubmitValidRows() {
    if (!data) {
      throw new Error("Data missing");
    }

    if (!dataState) {
      throw new Error("Data is not yet processed");
    }

    await submitRows({
      rows: dataState.validRows,
    });

    setSubmissionState((currState) => ({
      ...currState,
      isSubmitted: true,
    }));

    const newCorrectionData = data.filter((row) => dataState.invalidRows.includes(data.indexOf(row)));

    // If no data needs to be corrected, skip corrections
    if (newCorrectionData.length === 0) {
      handleSkipCorrections();
    } else {
      setData(newCorrectionData);
    }
  }

  function handleUpdateCorrectionTableCell({ row, column, value }: { row: number; column: string; value: string }) {
    if (!data) {
      return;
    }

    const newData = [...data];
    const rowNumber = Number(row);
    const oldRow = data[rowNumber];
    const newRow = { ...oldRow, [column]: value };
    newData.splice(rowNumber, 1, newRow);

    setData(newData);
  }

  function handleRemoveCorrectionTableRow({ row }: { row: number }) {
    if (!data) {
      return;
    }

    const newData = [...data];
    newData.splice(row, 1);

    setData(newData);

    if (newData.length === 0) {
      handleSkipCorrections();
    }
  }

  const loadExcelFile = useCallback(
    async (...args: Parameters<typeof parseExcel>) => {
      try {
        setIsParsingFile(true);
        const rows = await parseExcel(...args);
        setData(parseData(splitCombinedAddressColumn(rows as object[]), columns));
      } finally {
        setIsParsingFile(false);
      }
    },
    [columns],
  );

  useOnDropFiles(
    useCallback(
      async (files) => {
        const file = files?.[0].getAsFile();
        if (file) {
          try {
            const content = await parseFileContent(file);
            await loadExcelFile(content);
          } catch (error) {
            alert(error);
          }
        }
      },
      [loadExcelFile],
    ),
    !data && !isParsingFile,
  );

  useOnPaste(
    useCallback(
      (rawData) => {
        void loadExcelFile(rawData).catch((error) => alert(`Could not paste data: ${error}`));
      },
      [loadExcelFile],
    ),
    !data && !isParsingFile,
  );

  const handleUploadFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const target = e.currentTarget;
    const file = target.files?.[0];

    if (file) {
      try {
        setUploadedFile(file);
        const contents = await parseFileContent(file);
        await loadExcelFile(contents);

        // Reset `isSubmitted` upon each new file upload
        setSubmissionState((currState) => ({
          ...currState,
          isSubmitted: false,
        }));
      } catch (error) {
        alert(error);
        target.value = "";
      }
    }
  };

  const handleDownloadExampleFile = () => {
    if (!columns) {
      return;
    }

    void downloadExcel(`portfolio-address-upload-${sessionUser.project.name}`, [columns.map((x) => x.name)]);
  };

  const handleDownloadCorrectionFile = () => {
    if (!columns || !data) {
      return;
    }

    void downloadExcel(`portfolio-address-upload-corrections-${sessionUser.project.name}`, [
      columns.map((x) => x.name),
      ...data.map((row) => {
        // Strip first 2 fields (id and uploadStatus)
        const currRowData = Object.values(row).slice(2);

        currRowData.forEach((value, key) => {
          if (value === undefined) {
            currRowData[key] = "";
          }
        });

        return currRowData as string[];
      }),
    ]);
  };

  const handleSkipCorrections = () => {
    setIsAllRowsProcessed(true);
  };

  const handleReset = () => {
    setData(undefined);
    setIsAllRowsProcessed(false);
    setSubmissionState({
      isSubmitted: false,
      progress: 0,
    });
    setIsParsingFile(false);
    setSubmissionSummary({
      amountCurrSubmitting: 0,
      amountSuccess: 0,
      amountFailure: 0,
      amountDuplicates: 0,
    });
  };

  const isLoading = isPendingPermittedProjects || isPendingPermittedBuildings;
  if (isLoading) {
    return <FullSizeLoader withPadding />;
  }

  const error = permittedProjectsError || permittedBuildingsError;
  if (!columns || error) {
    return <ErrorPage error={error ?? t("page.address-bulk-upload.error.no-columns")} />;
  }

  const isDataLoaded = isDefined(data) && isDefined(dataState);
  const isSubmitting = submissionSummary.amountCurrSubmitting > 0;

  if (!isDataLoaded && isParsingFile) {
    pageState = PageState.VALIDATING;
  } else if (isDataLoaded && !submissionState.isSubmitted && !isSubmitting) {
    pageState = PageState.VALIDATED;
  } else if (isDataLoaded && isSubmitting) {
    pageState = PageState.SUBMITTING;
  } else if (isDataLoaded && submissionState.isSubmitted && !isAllRowsProcessed) {
    pageState = PageState.SUBMITTED_WITH_POTENTIAL_CORRECTIONS;
  } else if (isAllRowsProcessed) {
    pageState = PageState.COMPLETED;
  }

  return (
    <DocumentPage
      containerBoundary="inherit"
      tabTitle={t("page.portfolio-address-bulk-upload.title")}
      breadcrumbs={
        <Breadcrumbs
          pages={[
            {
              name: t("page.portfolio.title"),
            },
            {
              name: t("page.portfolio-address-bulk-upload.title"),
            },
          ]}
        />
      }
      actions={
        pageState === PageState.INITIAL && (
          <UploadFileButton
            disabled={isSubmitting}
            accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
            onChange={handleUploadFile}
          >
            {t("page.portfolio-address-bulk-upload.btn.upload-excel")}
          </UploadFileButton>
        )
      }
    >
      <PageGrid.Row>
        {pageState === PageState.INITIAL && (
          <BulkUploadAddressesInstructionView
            permittedProjects={permittedProjects}
            onDownloadExampleFile={handleDownloadExampleFile}
          />
        )}
        {pageState === PageState.VALIDATING && (
          <PageGrid.Column size="100%">
            <SectionCard>
              <FullSizeLoader withPadding />
            </SectionCard>
          </PageGrid.Column>
        )}
        {pageState === PageState.VALIDATED && (
          <BulkUploadAddressesValidationResultView
            dataState={dataState!}
            permittedProjects={permittedProjects}
            maxAmountCorrections={MAX_AMOUNT_CORRECTION_ROWS}
            onCancel={handleReset}
            onUploadFile={handleUploadFile}
            onSubmitValidRows={handleSubmitValidRows}
            onDownloadExampleFile={handleDownloadExampleFile}
          />
        )}
        {pageState === PageState.SUBMITTING && (
          <BulkUploaadAddressesSubmittingView
            amountSubmitting={submissionSummary.amountCurrSubmitting}
            progress={submissionState.progress}
          />
        )}
        {pageState === PageState.SUBMITTED_WITH_POTENTIAL_CORRECTIONS && (
          <>
            <BulkUploadAddressesSubmissionResultView submissionSummary={submissionSummary} />
            {data && data.length > 0 && dataState && (
              <>
                <PageGrid.Column size="100%">
                  <h2 className="text-heading-2">
                    {t("page.portfolio-address-bulk-upload.corrections-needed", { count: data.length })}
                  </h2>
                </PageGrid.Column>
                <PageGrid.Column size="100%">
                  {dataState.invalidRows.length >= MAX_AMOUNT_CORRECTION_ROWS ? (
                    <SectionCard
                      title={t("page.portfolio-address-bulk-upload.section.correction-unavailable.title", {
                        count: MAX_AMOUNT_CORRECTION_ROWS,
                      })}
                      description={t("page.portfolio-address-bulk-upload.section.correction-unavailable.description")}
                    >
                      <Button variant="ghostPrimary" icon={<Download01Icon />} onClick={handleDownloadCorrectionFile}>
                        {t("page.portfolio-address-bulk-upload.btn.download-correction-file")}
                      </Button>
                      <Notice
                        variant="special"
                        message={t("page.portfolio-address-bulk-upload.section.correction-unavailable.notice")}
                      />
                      <Inline gap="sm" justify="end" wrap={false}>
                        <Button variant="soft" icon={<DotsGridIcon />} onClick={handleSkipCorrections}>
                          {t("page.portfolio-address-bulk-upload.btn.skip-corrections")}
                        </Button>
                        <UploadFileButton
                          accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                          onChange={handleUploadFile}
                        >
                          {t("page.portfolio-address-bulk-upload.btn.upload-corrected-excel")}
                        </UploadFileButton>
                      </Inline>
                    </SectionCard>
                  ) : (
                    <SectionCard title={t("page.portfolio-address-bulk-upload.section.correction-available.title")}>
                      <DataTable
                        data={data}
                        columns={columns}
                        dataState={dataState}
                        onRemoveRow={(row) => handleRemoveCorrectionTableRow({ row })}
                        onUpdateValue={(row, column, value) =>
                          handleUpdateCorrectionTableCell({
                            row,
                            column,
                            value,
                          })
                        }
                        disabled={isSubmitting}
                      />
                      <footer className="mt-space-md flex w-full justify-end gap-space-sm">
                        <Button variant="soft" onClick={handleSkipCorrections}>
                          {t("page.portfolio-address-bulk-upload.btn.skip-corrections")}
                        </Button>
                        <Button onClick={handleSubmitValidRows} disabled={!dataState.valid}>
                          {t("page.portfolio-address-bulk-upload.btn.import-addresses")}
                        </Button>
                      </footer>
                    </SectionCard>
                  )}
                </PageGrid.Column>
              </>
            )}
          </>
        )}
        {pageState === PageState.COMPLETED && (
          <BulkUploadAddressesSubmissionSummaryView submissionSummary={submissionSummary} onRestart={handleReset} />
        )}
      </PageGrid.Row>
    </DocumentPage>
  );
}
